import { useRef, useState } from 'react';
import Dialog, { DialogError } from './Dialog.jsx';
import { api } from '../lib/api.js';

/**
 * The customer admin's single-use onboarding claim link (ENTRAID-350): the
 * same link the Marketplace path issues. The admin has no onGuard login yet;
 * claiming the link creates it. Display only: the server checks the operator
 * role, mints the link, and refuses staff and personal addresses.
 */

/** True when an operator may (re)issue the link. Mirrors the server (lib/onboarding-invite.mjs). */
export function canIssueOnboardingLink(customer) {
  return customer?.status === 'registered' && !customer.onboardingTokenUsedAt && !customer.adminEmail;
}

function formatExpiry(iso) {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? null : new Date(t).toLocaleString();
}

/**
 * The issued link with a Copy button, the expiry, and what happened to the
 * email. `result` is the server's { claimUrl, claimExpiresAt, invite }.
 */
export function ClaimLinkResult({ result }) {
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);
  const expires = formatExpiry(result.claimExpiresAt);
  const invite = result.invite ?? {};

  async function copy() {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(result.claimUrl);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) inputRef.current?.select();
    setCopied(ok);
  }

  return (
    <div className="space-y-2" data-testid="claim-link">
      <p className="text-sm text-ipc-ink">
        Send this link to the customer&apos;s admin. It works once{expires ? `, until ${expires}` : ''}, and creates
        their onGuard login.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          readOnly
          aria-label="Onboarding link for the customer admin"
          value={result.claimUrl}
          onFocus={(e) => e.target.select()}
          className="input w-full font-mono text-xs sm:w-[32rem]"
        />
        <button type="button" className="btn-secondary text-sm" onClick={copy}>Copy link</button>
      </div>
      {copied === true ? <p className="text-xs text-ipc-ink" role="status">Copied to the clipboard.</p> : null}
      {copied === false ? <p className="text-xs text-ipc-ink" role="status">Select the link above and copy it.</p> : null}
      {invite.emailed ? (
        <p className="text-sm text-ipc-ink" role="status" data-testid="claim-link-emailed">Emailed to {invite.to}.</p>
      ) : null}
      {invite.error ? (
        <p className="text-sm text-red-700" role="alert">{invite.error}</p>
      ) : null}
    </div>
  );
}

/**
 * The work email field and the "Email this link to the admin" option, shared
 * by the create form and the reissue dialog.
 */
export function AdminInviteFields({ idPrefix, email, onEmail, sendInvite, onSendInvite }) {
  const hasEmail = email.trim() !== '';
  return (
    <div className="space-y-2">
      <div>
        <label className="label" htmlFor={`${idPrefix}-admin-email`}>Customer admin work email</label>
        <input
          id={`${idPrefix}-admin-email`}
          type="email"
          className="input"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          placeholder="it-admin@customer.com"
          aria-describedby={`${idPrefix}-admin-email-help`}
          maxLength={254}
        />
        <p id={`${idPrefix}-admin-email-help`} className="mt-1 text-xs text-ipc-muted">
          Optional. A work address on the customer&apos;s own domain; IPC Global and personal addresses are refused.
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm text-ipc-ink">
        <input
          type="checkbox"
          checked={hasEmail && sendInvite}
          disabled={!hasEmail}
          onChange={(e) => onSendInvite(e.target.checked)}
        />
        Email this link to the admin
      </label>
    </div>
  );
}

/**
 * The operator action on a customer still waiting for its admin: "Reissue
 * onboarding link". Renders nothing unless `isOperator` and the customer is
 * still registered with an unclaimed link. The new link replaces the
 * previous one, which stops working. `label` names the action: the lifecycle
 * notice says "Issue onboarding link" (a customer created before ENTRAID-350
 * may never have had one).
 */
export function ReissueOnboardingLink({
  customer, isOperator, className = 'btn-secondary text-sm', label = 'Reissue onboarding link',
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const buttonRef = useRef(null);
  if (isOperator !== true || !canIssueOnboardingLink(customer)) return null;

  const name = customer.companyName || customer.customerId;

  function show() {
    setEmail(customer.onboardingInviteEmail ?? '');
    setSendInvite(true);
    setError(null);
    setResult(null);
    setOpen(true);
  }

  function close() {
    if (busy) return;
    setOpen(false);
  }

  async function reissue() {
    setBusy(true);
    setError(null);
    try {
      const trimmed = email.trim();
      const body = trimmed ? { adminEmail: trimmed, sendInvite } : {};
      setResult(await api.reissueOnboardingLink(customer.customerId, body));
    } catch (e) {
      setError(e?.message || 'The link was not reissued.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={className}
        onClick={show}
        aria-label={`${label} for ${name}`}
      >
        {label}
      </button>
      <Dialog
        open={open}
        title={`${label} for ${name}`}
        onClose={close}
        returnFocusRef={buttonRef}
        wide
        footer={result ? (
          <button type="button" className="btn-primary text-sm" onClick={close}>Done</button>
        ) : (
          <>
            <button type="button" className="btn-secondary text-sm" onClick={close} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary text-sm" onClick={reissue} disabled={busy}>
              {busy ? 'Reissuing...' : 'Reissue link'}
            </button>
          </>
        )}
      >
        {result ? (
          <ClaimLinkResult result={result} />
        ) : (
          <>
            <p>A new single-use link replaces the current one, which stops working at once.</p>
            <AdminInviteFields
              idPrefix="reissue"
              email={email}
              onEmail={setEmail}
              sendInvite={sendInvite}
              onSendInvite={setSendInvite}
            />
            <DialogError message={error} />
          </>
        )}
      </Dialog>
    </>
  );
}
